"use client";

import { useState, useEffect, useMemo } from "react";
import {
  PeeqdReportTabId,
  PEEQD_COMING_SOON_SECTIONS,
  isComingSoonSection,
  getDefaultReportTab,
  PeeqdToolId,
  PeeqdToolContent,
} from "@/config/peeqdContent";
import {
  buildDashboardWeekTabs,
  defaultWeekTabId,
  filterChangelogForWeek,
} from "@/libs/tracking/dashboardWeeks";
import type { WeekProfileSummaryData } from "@/libs/tracking/weekProfileSummary";
import {
  SectionPillTabs,
  FollowCountCards,
  FollowFilterTabs,
  FollowResultsCard,
  FollowListRow,
  FOLLOW_LIST_DISPLAY_LIMIT,
  type FollowFilter,
} from "@/components/peeqd/PeeqdPreview";
import PeeqdProfileHeader from "@/components/peeqd/PeeqdProfileHeader";
import AccountList from "@/components/peeqd/tracking/AccountList";
import DashboardWeekTabs from "@/components/peeqd/tracking/DashboardWeekTabs";
import ChangesSummaryCard from "@/components/peeqd/tracking/ChangesSummaryCard";
import WeekAnalysisPendingCard from "@/components/peeqd/tracking/WeekAnalysisPendingCard";
import DashboardDetailLoadingShell from "@/components/peeqd/tracking/DashboardDetailLoadingShell";
import ConnectAccountPanel from "@/components/peeqd/tracking/ConnectAccountPanel";
import RealLikesCard from "@/components/peeqd/tracking/RealLikesCard";
import ComingSoonCard from "@/components/peeqd/ComingSoonCard";
import type { LikesLeaderboardRow } from "@/libs/tracking/loadLikesLeaderboard";
import type { PeeqdProfileData } from "@/libs/peeqdProfilePreview";
// import PeeqdToolTitle from "@/components/peeqd/PeeqdToolTitle";
import type {
  FollowChangeItem,
  RelationshipItem,
  TrackedAccountSummary,
} from "@/components/peeqd/tracking/types";

type Side = "followers" | "following";

interface PeeqdToolDashboardZoneProps {
  toolContent: PeeqdToolContent;
  toolId?: PeeqdToolId;
  accounts: TrackedAccountSummary[];
  selectedAccountId: string | null;
  onSelectAccount: (id: string) => void;
  account: TrackedAccountSummary | null;
  changelog: FollowChangeItem[];
  followers: RelationshipItem[];
  following: RelationshipItem[];
  followersTrackedCount?: number;
  followingTrackedCount?: number;
  isScraping: boolean;
  listsReady?: boolean;
  activityReady?: boolean;
  likesReady?: boolean;
  isCollecting?: boolean;
  detailLoading?: boolean;
  profiles?: Record<string, PeeqdProfileData>;
  onConnectAccount?: (username: string) => Promise<void>;
  connectLoading?: boolean;
  connectError?: string | null;
}

function avatarIndexFor(seed: string): number {
  let h = 0;
  for (const c of seed) h = (h + c.charCodeAt(0)) % 64;
  return h;
}

function RealFollowersCard({
  changelog,
  followers,
  following,
  isBaseline = false,
  profileTotals,
  trackedCounts,
}: {
  changelog: FollowChangeItem[];
  followers: RelationshipItem[];
  following: RelationshipItem[];
  isBaseline?: boolean;
  profileTotals?: { followers: number; following: number };
  trackedCounts?: { followers: number; following: number };
}) {
  const [side, setSide] = useState<Side>("following");
  const [filter, setFilter] = useState<FollowFilter>("new");

  useEffect(() => {
    setFilter("new");
  }, [side]);

  const sideChangelog = changelog.filter((ev) => ev.side === side);
  const gained = sideChangelog.filter((ev) => ev.changeType === "follow").length;
  const lost = sideChangelog.filter((ev) => ev.changeType === "unfollow").length;
  const relationshipList = side === "followers" ? followers : following;

  const stats = {
    followers: {
      total: profileTotals?.followers ?? followers.length,
      gained: isBaseline
        ? followers.filter((it) => it.isRecent).length
        : changelog.filter((ev) => ev.side === "followers" && ev.changeType === "follow").length,
      lost: changelog.filter((ev) => ev.side === "followers" && ev.changeType === "unfollow").length,
    },
    following: {
      total: profileTotals?.following ?? following.length,
      gained: isBaseline
        ? following.filter((it) => it.isRecent).length
        : changelog.filter((ev) => ev.side === "following" && ev.changeType === "follow").length,
      lost: changelog.filter((ev) => ev.side === "following" && ev.changeType === "unfollow").length,
    },
  };

  const recentCount = relationshipList.filter((it) => it.isRecent).length;

  const trackedTotal =
    side === "followers"
      ? (trackedCounts?.followers ?? relationshipList.length)
      : (trackedCounts?.following ?? relationshipList.length);

  const counts = {
    new: isBaseline ? recentCount : gained,
    lost,
    all: trackedTotal,
  };

  const statusByUsername = new Map<string, "new" | "lost">();
  for (const ev of sideChangelog) {
    statusByUsername.set(ev.username, ev.changeType === "follow" ? "new" : "lost");
  }

  const avatarByUsername = new Map(
    relationshipList.map((it) => [it.username, it.avatarUrl ?? null])
  );

  const filteredRows =
    filter === "new"
      ? isBaseline
        ? relationshipList
            .filter((it) => it.isRecent)
            .map((it) => ({
              id: it.id,
              username: it.username,
              displayName: it.displayName,
              avatarIndex: it.avatarIndex,
              avatarUrl: it.avatarUrl ?? null,
              isRecent: true,
            }))
        : sideChangelog
            .filter((ev) => ev.changeType === "follow")
            .map((ev) => ({
              id: ev.id,
              username: ev.username,
              displayName: ev.displayName,
              avatarIndex: avatarIndexFor(ev.username),
              avatarUrl: avatarByUsername.get(ev.username) ?? null,
              status: "new" as const,
            }))
      : filter === "lost"
        ? sideChangelog
            .filter((ev) => ev.changeType === "unfollow")
            .map((ev) => ({
              id: ev.id,
              username: ev.username,
              displayName: ev.displayName,
              avatarIndex: avatarIndexFor(ev.username),
              avatarUrl: avatarByUsername.get(ev.username) ?? null,
              status: "lost" as const,
            }))
        : relationshipList.map((it) => ({
            id: it.id,
            username: it.username,
            displayName: it.displayName,
            avatarIndex: it.avatarIndex,
            avatarUrl: it.avatarUrl ?? null,
            status: statusByUsername.get(it.username),
          }));

  const visibleRows = filteredRows.slice(0, FOLLOW_LIST_DISPLAY_LIMIT);

  return (
    <div className="space-y-3">
      <FollowCountCards side={side} onSideChange={setSide} stats={stats} />
      <FollowFilterTabs active={filter} onChange={setFilter} counts={counts} />

      <FollowResultsCard
        header={
          filter === "all" && visibleRows.length > 0 ? (
            <p className="text-center text-[11.5px] font-medium text-muted">
              Showing {visibleRows.length} most recent
            </p>
          ) : undefined
        }
      >
        {visibleRows.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-muted">
            {isBaseline
              ? `No ${filter} ${side} found in your starting snapshot.`
              : `No ${filter} ${side} this week.`}
          </div>
        ) : (
          <div className="divide-y divide-line">
            {visibleRows.map((row, i) => (
              <FollowListRow
                key={row.id}
                index={i}
                username={row.username}
                displayName={row.displayName}
                avatarIndex={row.avatarIndex}
                avatarUrl={"avatarUrl" in row ? row.avatarUrl : null}
                status={"status" in row ? row.status : undefined}
                isRecent={"isRecent" in row ? row.isRecent : undefined}
                useNumberedMock={false}
              />
            ))}
          </div>
        )}
      </FollowResultsCard>
    </div>
  );
}

export default function PeeqdToolDashboardZone({
  toolContent: _toolContent,
  toolId,
  accounts,
  selectedAccountId,
  onSelectAccount,
  account,
  changelog,
  followers,
  following,
  followersTrackedCount = 0,
  followingTrackedCount = 0,
  isScraping,
  listsReady = false,
  activityReady = false,
  likesReady = false,
  isCollecting = false,
  detailLoading = false,
  profiles = {},
  onConnectAccount,
  connectLoading = false,
  connectError = null,
}: PeeqdToolDashboardZoneProps) {
  const hasAccount = account != null;

  const profile = useMemo((): PeeqdProfileData | null => {
    if (!account) return null;
    return profiles[account.id] ?? account.profile ?? null;
  }, [account, profiles]);

  const effectiveListsReady =
    listsReady || Boolean(account?.scrape.readiness?.lists);
  const effectiveActivityReady =
    activityReady || Boolean(account?.scrape.readiness?.activitySummary);
  const effectiveLikesReady =
    likesReady || Boolean(account?.scrape.readiness?.likes);
  const effectiveCollecting =
    (isCollecting || isScraping) && !effectiveActivityReady;
  const showListsCollecting = !effectiveListsReady && (detailLoading || isScraping);
  const showSummaryCollecting = effectiveListsReady && !effectiveActivityReady;
  const defaultTab = getDefaultReportTab(toolId);
  const [section, setSection] = useState<PeeqdReportTabId>(defaultTab);

  const weekTabs = useMemo(
    () =>
      account
        ? buildDashboardWeekTabs(
            changelog,
            effectiveActivityReady ? account.scrape.lastSuccessAt : null,
            account.createdAt
          )
        : [],
    [account, changelog, effectiveActivityReady]
  );

  const [weekTabId, setWeekTabId] = useState(() => defaultWeekTabId(weekTabs));

  const weekTabIds = useMemo(
    () => weekTabs.map((tab) => tab.id).join("|"),
    [weekTabs]
  );

  useEffect(() => {
    if (!account) return;
    setWeekTabId((prev) => {
      if (weekTabs.some((tab) => tab.id === prev)) return prev;
      return defaultWeekTabId(weekTabs);
    });
  }, [account?.id, weekTabIds]);

  const activeWeek =
    weekTabs.find((w) => w.id === weekTabId) ?? weekTabs[0] ?? null;
  const weekChangelog = activeWeek
    ? filterChangelogForWeek(changelog, activeWeek)
    : changelog;
  const weekLabel = activeWeek?.label ?? "";
  const isPendingWeek = activeWeek?.isPending === true;
  const isBaselineWeek = activeWeek?.kind === "baseline";
  const [weekSummary, setWeekSummary] = useState<WeekProfileSummaryData | null>(null);
  const [weekSummaryLoading, setWeekSummaryLoading] = useState(false);
  const [likesRows, setLikesRows] = useState<LikesLeaderboardRow[]>([]);
  const [likesTotal, setLikesTotal] = useState(0);
  const [likesLoading, setLikesLoading] = useState(false);

  useEffect(() => {
    if (!account || !activeWeek || isPendingWeek || !effectiveActivityReady) {
      if (!effectiveActivityReady) {
        setWeekSummary((prev) => (prev == null ? prev : null));
      }
      return;
    }

    let cancelled = false;
    setWeekSummaryLoading(true);

    const params = new URLSearchParams({
      startKey: activeWeek.startKey,
      endKey: activeWeek.endKey,
      weekLabel: activeWeek.label,
      periodKind: activeWeek.kind,
    });

    fetch(`/api/tracking/accounts/${account.id}/activity-summary?${params}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("summary failed"))))
      .then((data) => {
        if (!cancelled) setWeekSummary(data.summary ?? null);
      })
      .catch(() => {
        if (!cancelled) setWeekSummary(null);
      })
      .finally(() => {
        if (!cancelled) setWeekSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [account?.id, activeWeek?.id, isPendingWeek, effectiveActivityReady]);

  useEffect(() => {
    if (!account || !activeWeek || isPendingWeek || !effectiveLikesReady) {
      if (!effectiveLikesReady) {
        setLikesRows((prev) => (prev.length === 0 ? prev : []));
        setLikesTotal((prev) => (prev === 0 ? prev : 0));
      }
      return;
    }

    let cancelled = false;
    setLikesLoading(true);

    const params = new URLSearchParams({
      startKey: activeWeek.startKey,
      endKey: activeWeek.endKey,
      periodKind: activeWeek.kind,
    });

    fetch(`/api/tracking/accounts/${account.id}/likes-leaderboard?${params}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("likes failed"))))
      .then((data) => {
        if (!cancelled) {
          setLikesRows(data.leaderboard?.rows ?? []);
          setLikesTotal(data.leaderboard?.totalLikes ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLikesRows([]);
          setLikesTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLikesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [account?.id, activeWeek?.id, isPendingWeek, effectiveLikesReady]);

  useEffect(() => {
    setSection(getDefaultReportTab(toolId));
  }, [toolId]);

  const handleSectionChange = (tab: PeeqdReportTabId) => {
    setSection(tab);
  };

  return (
    <section className="min-h-screen border-b border-line bg-bg">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col lg:flex-row lg:items-start">
        <div className="flex w-full shrink-0 flex-col items-start border-b border-line px-4 py-6 sm:px-6 lg:w-[min(320px,32%)] lg:border-b-0 lg:border-r lg:px-6 lg:py-8">
          <p className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">Your dashboard</p>
          {hasAccount ? (
            <>
              <p className="mt-1 text-[14px] font-semibold text-ink">Connected accounts</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted">
                Select an account to view live results on the right.
              </p>
              <div className="mt-5 w-full">
                <AccountList
                  accounts={accounts}
                  selectedId={selectedAccountId}
                  onSelect={onSelectAccount}
                />
              </div>
            </>
          ) : (
            <>
              <p className="mt-1 text-[14px] font-semibold text-ink">Add an account to start tracking</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted">
                <span className="lg:hidden">Connect a public Instagram profile below to begin your first scrape.</span>
                <span className="hidden lg:inline">Connect a public Instagram profile on the right to begin your first scrape.</span>
              </p>
            </>
          )}
        </div>

        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-stretch py-5 pl-4 pr-4 sm:py-6 sm:pl-5 sm:pr-6 lg:py-7 lg:pl-6 lg:pr-8">
          {!hasAccount ? (
            onConnectAccount ? (
              <ConnectAccountPanel
                onConnect={onConnectAccount}
                loading={connectLoading}
                error={connectError}
              />
            ) : null
          ) : profile ? (
            <>
              <div className="mb-4 w-full overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
                <PeeqdProfileHeader profile={profile} isScraping={isScraping} />
              </div>

              {showListsCollecting ? (
                <DashboardDetailLoadingShell section={section} />
              ) : (
                <>
                  <DashboardWeekTabs
                    weeks={weekTabs}
                    activeId={weekTabId}
                    onChange={setWeekTabId}
                  />

                  {isPendingWeek ? (
                    <WeekAnalysisPendingCard
                      weekLabel={weekLabel}
                      username={account.instagramUsername}
                      availableFromLabel={activeWeek?.availableFromLabel ?? "Monday"}
                    />
                  ) : (
                    <>
                      {showSummaryCollecting ? (
                        <ChangesSummaryCard
                          kind={activeWeek?.kind ?? "weekly"}
                          nextReportLabel={activeWeek?.nextReportLabel}
                          isCollecting
                          weekLabel={weekLabel}
                        />
                      ) : weekSummaryLoading ? (
                        <DashboardDetailLoadingShell section={section} variant="summary" />
                      ) : weekSummary ? (
                        <ChangesSummaryCard
                          data={weekSummary}
                          kind={activeWeek?.kind ?? "weekly"}
                          nextReportLabel={activeWeek?.nextReportLabel}
                        />
                      ) : null}

                      <div className="mt-4 w-full">
                        <SectionPillTabs active={section} onChange={handleSectionChange} align="center" />
                      </div>

                      <div className="mt-4 w-full min-h-0 flex-1">
                        {section === "unfollows" ? (
                          effectiveListsReady ? (
                            <RealFollowersCard
                              changelog={weekChangelog}
                              followers={followers}
                              following={following}
                              isBaseline={isBaselineWeek}
                              profileTotals={
                                profile
                                  ? {
                                      followers: profile.followersCount,
                                      following: profile.followingCount,
                                    }
                                  : undefined
                              }
                              trackedCounts={{
                                followers: followersTrackedCount,
                                following: followingTrackedCount,
                              }}
                            />
                          ) : (
                            <DashboardDetailLoadingShell section={section} variant="lists" />
                          )
                        ) : section === "likes" ? (
                          <RealLikesCard
                            rows={likesRows}
                            totalLikes={likesTotal}
                            loading={!effectiveLikesReady || likesLoading}
                          />
                        ) : isComingSoonSection(section) ? (
                          <ComingSoonCard label={PEEQD_COMING_SOON_SECTIONS[section]} />
                        ) : null}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="mb-4 w-full overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
              <PeeqdProfileHeader
                profile={{
                  username: account.instagramUsername,
                  displayName: account.instagramUsername,
                  bio: "",
                  postsCount: 0,
                  followersCount: 0,
                  followingCount: 0,
                  mentions: [],
                  avatarIndex: 0,
                }}
                loading
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
